import {
  getEmailSent as getEmailSentApi,
  getEmailSentDTOsByEvent as getEmailSentDTOsByEventApi,
  countEmailSentDTOsByEvent as countEmailSentDTOsByEventApi,
} from "@/lib/api/clients/EmailSentClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { EmailSentDTOStatistics } from "@/types/EmailSentDTOStatistics";
import { Filter } from "@/types/Filter";
import { EmailSentDTO } from "@/types/EmailsSentDTO";

export async function getEmailSent(
  eventId: string,
  emailSentId: string,
  fields: string
): Promise<EmailSentDTOStatistics | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }
    if (!isValidObjectId(emailSentId)) {
      throw new Error("Invalid emailSentId");
    }
    if (!fields) {
      throw new Error("Fields parameter is required");
    }


    return await getEmailSentApi(eventId, emailSentId, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Get EmailSent DTOs with joined invitee data, filtering, searching, sorting, and pagination
 */
export async function getEmailSentDTOsByEvent(
  eventId: string,
  fields: string,
  filters: Filter[],
  searchTerm: string,
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  page: number,
  pageSize: number,
  /** PB-TG-7 — see the client: freezes the paged set at the caller's first
   *  load so inserts cannot shift later offsets. Optional; omitted = today's
   *  behaviour. */
  asOf?: number,
): Promise<EmailSentDTO[] | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("Fields parameter is required and cannot be empty");
    }

    // Validate searchTerm (it's optional, but if provided should be a string)
    if (searchTerm !== undefined && typeof searchTerm !== "string") {
      throw new Error("searchTerm must be a string");
    }

    // Validate sortBy
    if (!sortBy || sortBy.trim().length === 0) {
      throw new Error("sortBy parameter is required and cannot be empty");
    }

    // Validate sortOrder
    if (sortOrder !== "asc" && sortOrder !== "desc") {
      throw new Error("sortOrder must be either 'asc' or 'desc'");
    }

    // Validate pagination
    if (page < 0) {
      throw new Error("Page number cannot be negative");
    }

    if (pageSize <= 0) {
      throw new Error("Page size must be greater than 0");
    }

    if (pageSize > 150) {
      throw new Error("Page size cannot exceed 150");
    }

    // Validate filters array (if provided)
    if (filters && !Array.isArray(filters)) {
      throw new Error("Filters must be an array");
    }

    if (filters && filters.length > 0) {
      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];

        if (!filter.key || filter.key.trim().length === 0) {
          throw new Error(
            `Filter at index ${i}: key is required and cannot be empty`
          );
        }

        if (!filter.operator || filter.operator.trim().length === 0) {
          throw new Error(
            `Filter at index ${i}: operator is required and cannot be empty`
          );
        }

        if (filter.value === undefined || filter.value === null) {
          throw new Error(`Filter at index ${i}: value is required`);
        }

        // Validate operator is one of the allowed values
        const allowedOperators = ["=", "≠", ">", "≥", "<", "≤", "~", "∅", "⊙"];
        if (!allowedOperators.includes(filter.operator)) {
          throw new Error(
            `Filter at index ${i}: operator must be one of ${allowedOperators.join(
              ", "
            )}`
          );
        }
      }
    }


    return await getEmailSentDTOsByEventApi(
      eventId,
      fields,
      filters || [],
      searchTerm || "",
      sortBy,
      sortOrder,
      page,
      pageSize,
      asOf,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Count EmailSent DTOs with filtering and searching
 */
export async function countEmailSentDTOsByEvent(
  eventId: string,
  filters: Filter[],
  searchTerm: string,
  /** PB-TG-7 — see the client: freezes the total at the caller's first load so
   *  it describes the same set the pages are drawn from. Optional; omitted =
   *  today's behaviour. */
  asOf?: number
): Promise<number | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }

    // Validate searchTerm (it's optional, but if provided should be a string)
    if (searchTerm !== undefined && typeof searchTerm !== "string") {
      throw new Error("searchTerm must be a string");
    }

    // Validate filters array (if provided)
    if (filters && !Array.isArray(filters)) {
      throw new Error("Filters must be an array");
    }

    if (filters && filters.length > 0) {
      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];

        if (!filter.key || filter.key.trim().length === 0) {
          throw new Error(
            `Filter at index ${i}: key is required and cannot be empty`
          );
        }

        if (!filter.operator || filter.operator.trim().length === 0) {
          throw new Error(
            `Filter at index ${i}: operator is required and cannot be empty`
          );
        }

        if (filter.value === undefined || filter.value === null) {
          throw new Error(`Filter at index ${i}: value is required`);
        }

        // Validate operator is one of the allowed values
        const allowedOperators = ["=", "≠", ">", "≥", "<", "≤", "~", "∅", "⊙"];
        if (!allowedOperators.includes(filter.operator)) {
          throw new Error(
            `Filter at index ${i}: operator must be one of ${allowedOperators.join(
              ", "
            )}`
          );
        }
      }
    }


    return await countEmailSentDTOsByEventApi(
      eventId,
      filters || [],
      searchTerm || "",
      asOf,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

